import React, { useState, createContext, PropsWithChildren, useEffect } from "react";
import moment, { Moment } from "moment";
import debug from "./debug";

import { StatType } from "./tools/stats";
import { Labels } from "./types";
import { loadObject, removeItem, saveObject } from "./tools/storage";

const log = debug("SettingsProvider");

export interface Context {
    boardIds: string[];
    theme: "light" | "dark";
    viewMode: "fullscreen";
    editMode: boolean;
    instanceId: string;
    instanceType: "board_view";
}

export enum AppView {
    FUNNEL = "funnel",
    CFD = "cfd",
    REPLAY = "replay",
    STATS = "stats",
    TREE = "tree"
}

export enum Sorting {
    SELECTED_STATUSES_TIME_DESC,
    SELECTED_STATUSES_TIME_ASC,
    TOTAL_TIME_DESC,
    TOTAL_TIME_ASC
}

export interface Settings {
    view?: AppView;
    column?: string;
    autoupdate?: boolean;
    from?: Moment;
    to?: Moment;

    // STATS view specific
    itemSorting?: Sorting;
    showStats?: StatType[];
    statsShowUnselectedBars?: boolean;
    statsCompareAgainst?: StatType;

    // REPLAY view specific
    showPrevNextEvents?: boolean;
    showProgress?: boolean;

    // TREE view specific
    scaleBasedOnCount?: boolean;
    mergeStatuses?: boolean;
    firstStatusAsWorkflowStart?: boolean;
    lastStatusAsWorkflowEnd?: boolean;
    filterNodeWeightLessThan?: number;
    filterLevelsAbove?: number;
    nodeHeightRescale?: string; // "0" - unlimited height

    // FUNNEL view specific
    showDeadEnds?: boolean;
    numberColumn?: string;

    unitTag?: (strings: TemplateStringsArray, value: number | string) => string;
}

export const DEFAULT_CONTEXT: Context = {
    boardIds: [],
    theme: "light",
    viewMode: "fullscreen",
    editMode: false,
    instanceId: "0",
    instanceType: "board_view"
};

export const DEFAULT_SETTINGS: Partial<Settings> = {
    view: AppView.CFD,
    from: moment().startOf("day"),
    to: moment().startOf("day"),
    itemSorting: Sorting.SELECTED_STATUSES_TIME_DESC,
    showStats: [StatType.MAX, StatType.MEDIAN, StatType.P90],
    scaleBasedOnCount: true,
    mergeStatuses: true,
    firstStatusAsWorkflowStart: false,
    lastStatusAsWorkflowEnd: false,
    unitTag: (_, value) => `${value}`
};

type Props = {
    settings: Settings;
    context: Context;
    labels: Labels;
};

type ContextValue = {
    settings: Settings;
    context: Context;
    labels: Labels;
    setLabels: (newLabels: Partial<Labels> | null) => void;
};

export const SettingsContext = createContext({} as ContextValue);

export const SettingsProvider: React.FC<Props> = function ({
    settings,
    context,
    labels,
    children
}: PropsWithChildren<Props>) {
    const [effectiveLabels, setEffectiveLabels] = useState<Labels>(labels);
    const storeKey = `labels-orderedids-${context.instanceId}`;

    useEffect(() => {
        setEffectiveLabels(labels);

        const f = async (): Promise<void> => {
            const orderedIds = (await loadObject(storeKey)) as string[] | undefined;
            // do not load saved order if labels have changed
            if (
                orderedIds &&
                orderedIds.length === labels.orderedIds.length &&
                !orderedIds.some((id) => labels.names[id] === undefined)
            ) {
                const newLabels = { ...labels };
                newLabels.orderedIds = orderedIds;
                // regenerate order
                newLabels.order = orderedIds.reduce((acc, id, idx) => {
                    acc[id] = idx;
                    return acc;
                }, {} as { [id: string]: number });
                setEffectiveLabels(newLabels);
            }
        };
        f();
    }, [labels]); //eslint-disable-line

    const resetLabels = (): void => {
        setEffectiveLabels(labels);
        removeItem(storeKey);
    };

    const setLabels = (newLabels: Partial<Labels> | null): void => {
        if (newLabels === null) {
            return resetLabels();
        }

        if (newLabels.orderedIds) {
            const order: { [labelId: string]: number } = {};
            newLabels.orderedIds.forEach((labelId, idx) => (order[labelId] = idx));
            setEffectiveLabels({ ...effectiveLabels, orderedIds: newLabels.orderedIds, order });
            saveObject(storeKey, newLabels.orderedIds);
            log("labels order changed to ", newLabels.orderedIds);
        } else if (Object.keys(newLabels).length > 0) {
            throw new Error("setLabels can only set orderedIds at the momemt");
        }
    };

    return (
        <SettingsContext.Provider value={{ settings, context, labels: effectiveLabels, setLabels }}>
            {children}
        </SettingsContext.Provider>
    );
};
